import type React from "react";
import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";

import { BN } from "@utils/BN";

export interface BigNumberInputProps {
  decimals?: number;
  value?: BN | null;
  inputRef?: React.RefObject<HTMLInputElement>;
  placeholder?: string;
  disabled?: boolean;
  displayDecimals?: number;
  className?: string;
  onChange: (value: BN) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  inputTestId?: string;
}

export const BigNumberInput: React.FC<BigNumberInputProps> = ({
  decimals = 18,
  inputRef,
  displayDecimals: _displayDecimals,
  value,
  placeholder = "0.00",
  disabled = false,
  className,
  onChange,
  onFocus,
  onBlur,
  inputTestId,
}) => {
  const displayDecimals = _displayDecimals ?? decimals;

  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (value && value !== BN.ZERO) {
      setInputValue(BN.formatUnits(value, decimals).toString());
      return;
    }
    setInputValue("");
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedInput = e.target.value.replaceAll(",", "");

    const parsedValueBN = BN.parseUnits(formattedInput, decimals);

    if (parsedValueBN.isZero() || parsedValueBN.isNaN()) {
      setInputValue("");
      onChange(BN.ZERO);
      return;
    }
    onChange(parsedValueBN);
  };

  return (
    <NumericFormat
      data-testid={inputTestId ?? "big-number-input"}
      allowNegative={false}
      className={className}
      decimalScale={displayDecimals}
      disabled={disabled}
      getInputRef={(el: HTMLInputElement) => inputRef && (inputRef.current = el)}
      inputMode="decimal"
      placeholder={placeholder}
      thousandSeparator=","
      type="text"
      value={inputValue}
      allowLeadingZeros
      onBlur={onBlur}
      onChange={handleChange}
      onFocus={onFocus}
    />
  );
};
